import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  DoCheck,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

import { Vehicle } from 'src/app/api/owcc/models';
import { appInject } from 'src/app/app-injector';
import { MQS_MOBILE } from 'src/app/core/config/mq.config';
import { RouterEventsService } from 'src/app/core/services/router-events/router-events.service';
import { FcisRateInfo } from 'src/app/shared/financing/finance.models';
import { openRegionPicker, updateHeaderInfoHeight } from 'src/app/store/common/common.actions';
import {
  fromCarl,
  fromMq,
  selectIsInIntegratedMode,
  selectUsePriceDisplayComponent
} from 'src/app/store/common/common.selectors';
import { seamlessStoreFns } from 'src/app/store/common/common.utils';
import { financingUpdateIsFcisWidgetOpen } from 'src/app/store/financing/financing.actions';
import {
  selectDisplayRate,
  selectFcisRateInfo,
  selectIsFcisInitialized,
  selectIsFcisResultAvailable
} from 'src/app/store/financing/financing.selectors';
import { fromRegion } from 'src/app/store/mbcc/mbcc.selectors';
import { filterTruthyNonNullable } from 'src/app/utils/functions/rx-filters';
import { BusinessType } from 'src/app/utils/global-constants';
import { WINDOW } from 'src/app/utils/window.token';

import { EnvSpecificTitleComponent } from '../env-specific-title/env-specific-title.component';
import { FinanceService } from '../financing/finance.service';
import { FootnoteHint } from '../footnotes/footnote-hint/footnote-hint.enum';
import { NbaConfigurations } from '../nba-bar/nba.model';
import { NbaService } from '../nba-bar/nba.service';
import { PermanentNbaButtonComponent } from '../permanent-nba/permanent-nba-button.component';
import { PermanentNbaContentComponent } from '../permanent-nba/permanent-nba-content.component';
import { TranslateAndObservePipe } from '../pipes/translate-and-observe/translate-and-observe.pipe';
import { TranslatePipe } from '../pipes/translate/translate.pipe';
import { PriceDisplayContainerComponent } from '../price-display/price-display-container/price-display-container.component';
import { WishlistSaveButtonComponent } from '../wishlist-save-button/wishlist-save-button.component';
import { WishlistSaveButtonModule } from '../wishlist-save-button/wishlist-save-button.module';

import { FinanceCalculatorStatus } from './finance-calculator-status.enum';
import { FinanceDisplayComponent } from './finance-display/finance-display.component';
import { HeaderInfoStore } from './header-info.store';

export interface FinancingConfig {
  useFcis: boolean;
  showPrice: boolean;
}

@Component({
  selector: 'cc-header-info',
  templateUrl: './header-info.component.html',
  styleUrl: './header-info.component.scss',
  providers: [HeaderInfoStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LetDirective,
    NgTemplateOutlet,
    NgClass,
    EnvSpecificTitleComponent,
    PermanentNbaContentComponent,
    PermanentNbaButtonComponent,
    FinanceDisplayComponent,
    PriceDisplayContainerComponent,
    AsyncPipe,
    TranslatePipe,
    TranslateAndObservePipe,
    WishlistSaveButtonModule,
    WishlistSaveButtonComponent
  ]
})
export class HeaderInfoComponent implements DoCheck, OnInit, OnDestroy {
  @Input() public isSummary = false;

  public readonly fcisRateInfo$: Observable<FcisRateInfo | undefined> = this.store.select(selectFcisRateInfo);

  public readonly isMobile$: Observable<boolean> = this.store.select(fromMq.selectIsMq3Max);

  public readonly financingConfigForHeader$: Observable<FinancingConfig> = combineLatest([
    this.financeService.isFcisEnabledForCurrentCustomerType(),
    this.store.select(fromCarl.selectConfig<boolean>('financial.showPrice', false))
  ]).pipe(map(([useFcis, showPrice]) => ({ useFcis, showPrice })));

  public readonly fcisStatus$: Observable<FinanceCalculatorStatus> = combineLatest([
    this.financeService.isFcisEnabledForCurrentCustomerType(),
    this.store.select(selectIsFcisInitialized)
  ]).pipe(
    map(([isEnabled, isFcisInitialized]) =>
      isFcisInitialized && isEnabled ? FinanceCalculatorStatus.Enabled : FinanceCalculatorStatus.Disabled
    )
  );

  public readonly isFcisResultAvailable$: Observable<boolean> = this.store.select(selectIsFcisResultAvailable);

  public readonly displayRates$: Observable<boolean | undefined> = this.store.select(selectDisplayRate);

  public readonly showFinanceCalculator$: Observable<boolean> = combineLatest([
    this.fcisStatus$,
    this.isFcisResultAvailable$
  ]).pipe(
    map(
      ([fcisStatus, isFcisResultAvailable]) => fcisStatus === FinanceCalculatorStatus.Enabled && isFcisResultAvailable
    )
  );

  public readonly usePLS$ = this.store.select(selectUsePriceDisplayComponent);

  public readonly vehicle$ = this.headerInfoStore.vehicle$;

  public readonly priceDisplayParams = {
    footnoteContext: 'headerinfo',
    useUILabel: true
  };

  public readonly isRegionPickerEnabled$: Observable<boolean> = this.store.select(
    fromCarl.selectConfig<boolean>('financial.showRegionPicker', false)
  );

  public readonly postalCode$: Observable<string | undefined> = this.store.select(fromRegion.selectPostalCode);
  public readonly isRegionSelected$: Observable<boolean> = this.store.select(fromRegion.selectIsRegionSelected);
  public readonly hasVehicleAPrice$ = this.headerInfoStore.hasVehicleAPrice$;
  public readonly isRegionPickerEnabledAndVehicleHasPrice$ = combineLatest([
    this.isRegionPickerEnabled$,
    this.hasVehicleAPrice$
  ]).pipe(map(([isRegionPickerEnabled, hasVehicleAPrice]) => isRegionPickerEnabled && hasVehicleAPrice));
  public readonly isRegionPickerEnabledAndVehicleHasNoPrice$ = combineLatest([
    this.isRegionPickerEnabled$,
    this.hasVehicleAPrice$
  ]).pipe(map(([isRegionPickerEnabled, hasVehicleAPrice]) => isRegionPickerEnabled && !hasVehicleAPrice));
  public readonly isVehicleRequestTriggeredByPostalCodeChangeInProgress$ =
    this.headerInfoStore.isVehicleRequestTriggeredByPostalCodeChangeInProgress$;

  public readonly financeCalculatorStatus = FinanceCalculatorStatus;

  public readonly footnoteHint = FootnoteHint;

  public businessType$: Observable<string> = seamlessStoreFns.observeBusinessTypeInSeamlessStore();
  public businessType: BusinessType = BusinessType.B2C;

  public subnavVisible = false;
  public subnavHasBeenOpenedAtLeastOnce = false;
  public readonly mq$ = this.store.select(fromMq.selectMediaQuery);
  public readonly isInIntegratedMode$ = this.store.select(selectIsInIntegratedMode);

  public readonly hasPermanentNbas$: Observable<boolean> = this.store
    .pipe(fromCarl.selectConfigOnce<NbaConfigurations>('nbas'))
    .pipe(map(nbaConfig => nbaConfig.permanentNbas?.length > 0));

  public isWishlistConfigured$: Observable<boolean> | undefined = this.nbaService.isWishlistConfigured$();

  private observeBusinessTypeInSeamlessStoreSubscription: Subscription | undefined;
  private headerHeight = 0;

  public constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly headerInfoStore: HeaderInfoStore,
    private readonly financeService: FinanceService,
    private readonly store: Store,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly nbaService: NbaService,
    private readonly renderer: Renderer2,
    private readonly routerEventsService: RouterEventsService,
    @Inject(WINDOW) private readonly window: Window
  ) {}
  @Input()
  public set vehicle(vehicle: Vehicle) {
    this.headerInfoStore.updateVehicle(vehicle);
  }

  public ngOnInit(): void {
    this.observeBusinessTypeInSeamlessStoreSubscription = seamlessStoreFns
      .observeBusinessTypeInSeamlessStore()
      .subscribe(businessType => {
        this.businessType = businessType;
        this.changeDetectorRef.markForCheck();
      });

    this.observeBreakpointChange();
    this.observeRouteChange();
  }

  public ngDoCheck(): void {
    const currentHeaderHeight = this.elementRef?.nativeElement?.getBoundingClientRect()?.height;
    if (this.headerHeight !== currentHeaderHeight) {
      this.headerHeight = currentHeaderHeight;
      this.store.dispatch(updateHeaderInfoHeight({ height: this.headerHeight }));
    }
  }

  public ngOnDestroy(): void {
    this.observeBusinessTypeInSeamlessStoreSubscription?.unsubscribe();
    this.removeGlobalListener();
  }

  public openFinancialCalculator(): void {
    this.store.dispatch(financingUpdateIsFcisWidgetOpen({ isFcisWidgetOpen: true }));
  }

  public openRegionPicker(): void {
    this.store.dispatch(openRegionPicker());
  }

  public toggleSubnavVisibility(): void {
    this.subnavVisible = !this.subnavVisible;

    // workaround to set subnavHasBeenOpenedAtLeastOnce to true, on the first opening. so that tracking is working properly.
    // add global listener as soon the click event is processed
    this.window.setTimeout((): void => {
      this.subnavHasBeenOpenedAtLeastOnce = true;
      if (this.subnavVisible) {
        this.addGlobalListener();
      }
    }, 0);
  }

  public hideSubnav(): void {
    this.subnavVisible = false;
    this.removeGlobalListener();
  }

  private appRootUnlistener: () => void = () => {}; // renderer2.listen() cleanup function

  private observeBreakpointChange(): void {
    this.mq$.pipe(filterTruthyNonNullable(), takeUntilDestroyed(appInject(DestroyRef))).subscribe(mq => {
      const shouldToggle = mq && MQS_MOBILE.includes(mq) && this.subnavVisible;
      if (shouldToggle) {
        this.hideSubnav();
      }
    });
  }

  private observeRouteChange(): void {
    this.routerEventsService.navigationEnd$.pipe(takeUntilDestroyed(appInject(DestroyRef))).subscribe(() => {
      this.onGlobalInteraction();
    });
  }

  private addGlobalListener(): void {
    const appElement: HTMLElement | null = this.elementRef.nativeElement.closest('cc-app-container');

    if (appElement) {
      this.appRootUnlistener = this.renderer.listen(appElement, 'click', (event: Event) => {
        const targetElement = event.target as HTMLElement;

        if (
          targetElement &&
          !targetElement.closest('.sub-navigation-container') &&
          !targetElement.closest('#subnav-toggle')
        ) {
          this.onGlobalInteraction();
        }
      });
    }
  }

  private onGlobalInteraction(): void {
    this.hideSubnav();
    this.removeGlobalListener();
    this.changeDetectorRef.markForCheck();
  }

  private removeGlobalListener(): void {
    this.appRootUnlistener();
  }
}
